/**
 * Money formatting helpers — single source of truth for currency symbols
 * so that every surface (checkout, invoice, billing center, payment result)
 * renders the SAME glyph for a given currency code.
 *
 * Symbols match the backend `pricing_service.CURRENCIES` table exactly.
 *   INR → ₹      USD → $      AED → AED
 *   EUR → €      GBP → £      HKD → HK$
 *
 * Usage:
 *   import { currencySymbol, formatMoney } from "../lib/money";
 *   formatMoney(4999, "INR")  // "₹4,999"
 *   formatMoney(699,  "HKD")  // "HK$699"
 */
const SYMBOLS = {
  INR: "\u20B9",   // ₹
  USD: "$",
  AED: "AED",
  EUR: "\u20AC",   // €
  GBP: "\u00A3",   // £
  HKD: "HK$",
  SGD: "S$",
  MYR: "RM",
  IDR: "Rp",
};

export function currencySymbol(code) {
  if (!code) return "";
  const c = String(code).toUpperCase();
  return SYMBOLS[c] || c;
}

/**
 * Format an amount with its currency symbol. Locale-aware grouping
 * (`toLocaleString`) so 4999 renders as 4,999 in en-IN / en-US.
 *
 * For prefix symbols (₹, $, €, £, HK$) — no space (e.g. ₹4,999).
 * For ISO-letter symbols (AED) — keeps a space for readability (AED 329).
 * "Not charged" / empty / null amounts return "—".
 */
export function formatMoney(amount, code) {
  if (amount === null || amount === undefined || amount === "") return "\u2014";
  const sym = currencySymbol(code);
  const n = Number(amount);
  if (Number.isNaN(n)) return "\u2014";
  const formatted = n.toLocaleString();
  // Two/three-letter symbols that need a space for readability.
  if (sym === "AED" || sym === "RM" || sym === "Rp") return `${sym} ${formatted}`;
  return `${sym}${formatted}`;
}
